import React, { useEffect, useState } from 'react';
import {
    useDispatch,
    useSelector,
} from 'react-redux';
import PropTypes from 'prop-types';

import PostingActions from 'app/shared/actions/PostingActions';

import { RoutingSelectors } from 'app/shared/selectors';
import { StyledNoBorderButton } from 'app/shared/StyledComponents/StyledComponents';
import { GA_EVENTS } from 'app/shared/constants/AnalyticsLeasesModuleConstants';
import analyticsUtils from 'app/client/utils/analyticsUtils';

import ConnectedModuleCardConstants from 'app/shared/constants/ConnectedModuleCardConstants';

import { StyledConnectedModuleCard } from './StyledComponents';
import ConnectedLeases from './components/ConnectedLeases';
import EmptyLeasesCard from './components/EmptyLeasesCard';

const {
    VIEW_ALL_LEASES_CLICK,
} = GA_EVENTS;

const ConnectedLeasesModule = () => {
    const dispatch = useDispatch();

    const listingId = useSelector(state => RoutingSelectors.makeListingId(state));

    const [leasesSummary, setLeasesSummary] = useState(null);

    useEffect(() => {
        // listingId is required
        if (!listingId) {
            return;
        }

        dispatch(PostingActions.property.getLeasesSummary(listingId))
            .then((response) => {
                setLeasesSummary(response);
            })
            .catch(() => {
                setLeasesSummary(false);
            });
    }, [dispatch, listingId]);

    const isLoading = leasesSummary === null;
    const isFailedRequest = leasesSummary === false;

    if (isLoading) {
        return (
            <StyledConnectedModuleCard
                title="Leases"
                isLoading={isLoading}
                spinnerHeight={ConnectedModuleCardConstants.MANAGE_TENANTS_SPINNER_HEIGHT}
                spinnerSize="md"
            />
        );
    }

    if (isFailedRequest) {
        return (
            <StyledConnectedModuleCard title="Leases" />
        );
    }

    const {
        leaseCards,
        dashboardLinkText,
        urlPath: dashboardUrlPath,
        ctaButtons,
    } = leasesSummary;

    const handleViewAllLeasesClick = () => {
        dispatch(analyticsUtils.track.GAEventV2(VIEW_ALL_LEASES_CLICK));
    };

    return (
        <StyledConnectedModuleCard
            className="ConnectedLeasesModule"
            title="Leases"
            ctaComponent={(leaseCards && leaseCards.length) ? (
                <StyledNoBorderButton
                    forwardedAs="a"
                    href={dashboardUrlPath}
                    onClick={handleViewAllLeasesClick}
                >
                    {dashboardLinkText}
                </StyledNoBorderButton>
            ) : null}
        >
            {(leaseCards && leaseCards.length) ? (
                <ConnectedLeases leaseCards={leaseCards} ctaButtons={ctaButtons} />
            ) : (
                <EmptyLeasesCard {...leasesSummary.onboardingModule} />
            )}

        </StyledConnectedModuleCard>
    );
};

ConnectedLeasesModule.propTypes = {
    leases: PropTypes.arrayOf(PropTypes.shape({
        leaseId: PropTypes.number,
        leaseStatusText: PropTypes.string.isRequired,
        leaseStatusType: PropTypes.string.isRequired,
        leaseThumbnailUrl: PropTypes.string.isRequired,
        leaseName: PropTypes.string.isRequired,
        leaseDescription: PropTypes.string.isRequired,
        ctaButtonText: PropTypes.string.isRequired,
        ctaButtonUrl: PropTypes.string.isRequired,
        overflowButtons: PropTypes.arrayOf(PropTypes.shape({
            text: PropTypes.string.isRequired,
            url: PropTypes.string.isRequired,
        })),
    })),
};

ConnectedLeasesModule.defaultProps = {
    leases: [],
};

export default ConnectedLeasesModule;
